import { SELECTION_COLUMN_ID } from 'ag-grid-community';
import type { GridState, GridStateKey } from 'ag-grid-community';
import { Array as A, Record as R, pipe } from 'effect';

import { isServiceColumn } from '@/components/AgGrid/helpers';
import { isSameJson } from '@/lib/effectHelpers';
import type { DocumentsGridPresetConfig } from '@/domain/documents/schemas';
import type { GridLayout, GridLayoutChanges, GridLayoutSort } from '@/domain/documents/gridPreset/schemas';
import { toSavedColumnLayout } from '@/domain/documents/savedColumnLayout';
import type { SavedColumn, SavedColumnLayout } from '@/domain/documents/savedColumnLayout';

// Conversions between the stored layout (preset config / slice / browser — always LTR) and AG Grid's GridState.
// A layout lists data columns only, through `toSavedColumnLayout` on every way in and out; the technical columns
// (service columns and AG Grid's checkbox column) come from their column definitions.

type Pin = 'left' | 'right';

const swapPin = (pin: Pin): Pin => (pin === 'left' ? 'right' : 'left');

const isSort = (item: { colId: string; sort?: string | null }): item is GridLayoutSort =>
  item.sort === 'asc' || item.sort === 'desc';

// A technical column as its column definition places it: LTR pinning and visibility. Its width is the definition's.
export type TechnicalColumn = Pick<SavedColumn, 'colId' | 'pinned' | 'hide'>;

// Which GridState keys a layout owns. A Record over every key, so a key AG Grid adds stops this file compiling until
// it is sorted into owned or not — `api.setState` resets every key it is not told to ignore.
const OWNED_GRID_STATE_KEYS: Record<GridStateKey, boolean> = {
  filter: true,
  sort: true,
  columnOrder: true,
  columnPinning: true,
  columnSizing: true,
  columnVisibility: true,
  aggregation: false,
  cellSelection: false,
  columnGroup: false,
  columnHeaderName: false,
  find: false,
  focusedCell: false,
  pagination: false,
  pivot: false,
  quickFilter: false,
  rowGroup: false,
  rowGroupExpansion: false,
  rowPinning: false,
  rowSelection: false,
  scroll: false,
  showValuesAs: false,
  sideBar: false,
  ssrmRowGroupExpansion: false,
  userColumns: false,
};

export const IGNORED_ON_APPLY: GridStateKey[] = pipe(
  Object.entries(OWNED_GRID_STATE_KEYS) as Array<[GridStateKey, boolean]>,
  A.filter(([, owned]) => !owned),
  A.map(([key]) => key),
);

// Whether a stateUpdated source is a key the layout owns (sources also carry 'gridInitializing' and 'api').
export const isLayoutStateKey = (source: string): boolean =>
  (OWNED_GRID_STATE_KEYS as Record<string, boolean>)[source] === true;

// A layout's columns, followed by the column definitions' columns it does not list (as the definitions have them).
// `setState` resets hiding and pinning on every column its state leaves out, so a column added after a preset was
// saved would otherwise show, unpinned, even when its definition hides it.
export const withMissingColumns = (columns: SavedColumnLayout, defaultColumns: SavedColumnLayout): SavedColumn[] => {
  const listed = new Set(columns.map(({ colId }) => colId));
  return [...columns, ...A.filter(defaultColumns, ({ colId }) => !listed.has(colId))];
};

// A preset as stored on the server → layout. A preset without data columns shows the column definitions as they
// come. Technical columns, keys the workspace does not apply and a column entry of the wrong shape are dropped
// (`toSavedColumnLayout`), so a preset saved by an older AG Grid compares equal to an unchanged grid.
export const layoutFromPresetConfig = (
  config: DocumentsGridPresetConfig | null | undefined,
  defaultColumns: SavedColumnLayout,
): GridLayout => {
  const columns = toSavedColumnLayout(config?.gridColumns ?? []);

  return {
    filters: config?.gridFilters ?? {},
    sorting: A.filter(config?.gridSorting ?? [], isSort).map(({ colId, sort }) => ({ colId, sort })),
    columns: columns.length > 0 ? withMissingColumns(columns, defaultColumns) : [...defaultColumns],
  };
};

export const toPresetConfig = (layout: GridLayout) => ({
  gridFilters: layout.filters,
  gridSorting: layout.sorting,
  gridColumns: [...toSavedColumnLayout(layout.columns)],
});

// Layout → the part of GridState the layout owns. `columns: []` leaves the column keys out, so the grid keeps the
// column definitions. `setState` unpins and shows every column its column keys leave out, so the technical columns
// are listed too, from their definitions (`technicalColumns`, LTR, in definition order): the leading-pinned ones before
// the data columns, the rest after, each on its pinned side (mirrored in RTL) and hidden as defined. Their widths are
// left to the definitions. The checkbox column, which AG Grid generates, is listed first and pinned to the leading
// edge; `placeSelectionColumn` moves it next to the expander afterwards.
//
// The filter model is copied: AG Grid keeps the object it is given, and the slice's copy must not change with it.
// `setState` with an ignore list applies the column keys as a partial column state (AG Grid sets
// `partialColumnState = !!propertiesToIgnore`), which is why `IGNORED_ON_APPLY` must always be passed.
export const toGridState = (
  layout: GridLayout,
  { rtl, technicalColumns = [] }: { rtl: boolean; technicalColumns?: ReadonlyArray<TechnicalColumn> },
): GridState => {
  const physical = (pin: Pin) => (rtl ? swapPin(pin) : pin);
  const { columns } = layout;
  const [trailingTechnical, leadingTechnical] = A.partition(technicalColumns, ({ pinned }) => pinned === 'left');
  const all: ReadonlyArray<TechnicalColumn> = [
    { colId: SELECTION_COLUMN_ID, pinned: 'left', hide: false },
    ...leadingTechnical,
    ...columns,
    ...trailingTechnical,
  ];
  const idsWhere = (keep: (column: TechnicalColumn) => boolean) =>
    pipe(
      all,
      A.filter(keep),
      A.map(({ colId }) => colId),
    );
  const pinnedTo = (side: Pin) => idsWhere(({ pinned }) => pinned != null && physical(pinned) === side);

  const columnState: GridState =
    columns.length === 0
      ? {}
      : {
          columnOrder: { orderedColIds: idsWhere(() => true) },
          columnPinning: { leftColIds: pinnedTo('left'), rightColIds: pinnedTo('right') },
          columnVisibility: { hiddenColIds: idsWhere(({ hide }) => hide) },
          columnSizing: {
            columnSizingModel: pipe(
              columns,
              A.filter(({ width, flex }) => width != null || flex != null),
              A.map(({ colId, width, flex }) => ({
                colId,
                ...(width != null ? { width } : {}),
                ...(flex != null ? { flex } : {}),
              })),
            ),
          },
        };

  return {
    filter: { filterModel: structuredClone(layout.filters) },
    sort: { sortModel: layout.sorting.map(({ colId, sort }) => ({ colId, sort })) },
    ...columnState,
  };
};

// GridState (as reported by onStateUpdated / getState) → layout, LTR, data columns only.
export const fromGridState = (state: GridState, { rtl }: { rtl: boolean }): GridLayout => {
  const logical = (pin: Pin) => (rtl ? swapPin(pin) : pin);
  const left = new Set(state.columnPinning?.leftColIds ?? []);
  const right = new Set(state.columnPinning?.rightColIds ?? []);
  const hidden = new Set(state.columnVisibility?.hiddenColIds ?? []);
  const sizes = new Map((state.columnSizing?.columnSizingModel ?? []).map((size) => [size.colId, size]));
  const physicalPin = (colId: string): Pin | null => {
    if (left.has(colId)) return 'left';
    if (right.has(colId)) return 'right';
    return null;
  };

  return {
    filters: (state.filter?.filterModel ?? {}) as Record<string, unknown>,
    sorting: A.filter(state.sort?.sortModel ?? [], isSort).map(({ colId, sort }) => ({ colId, sort })),
    columns: toSavedColumnLayout(
      (state.columnOrder?.orderedColIds ?? []).map((colId) => {
        const pin = physicalPin(colId);
        const size = sizes.get(colId);
        return { colId, width: size?.width, flex: size?.flex, hide: hidden.has(colId), pinned: pin && logical(pin) };
      }),
    ),
  };
};

// The part of a layout the user sees change: filters as-is, sort by column and direction in order, data columns in
// the saved shape (`toSavedColumnLayout`), in order.
export const normalizeLayout = (layout: GridLayout) => ({
  filters: layout.filters,
  sorting: layout.sorting.map(({ colId, sort }) => ({ colId, sort })),
  columns: toSavedColumnLayout(layout.columns),
});

// The parts of `current` that differ from `saved`, compared as `normalizeLayout` puts them.
export const layoutChanges = (saved: GridLayout, current: GridLayout): GridLayoutChanges => {
  const before = normalizeLayout(saved);
  const after = normalizeLayout(current);
  const changed = (part: keyof GridLayout) => !isSameJson(before[part], after[part]);
  return {
    ...(changed('filters') && { filters: current.filters }),
    ...(changed('sorting') && { sorting: current.sorting }),
    ...(changed('columns') && { columns: current.columns }),
  };
};

export const hasLayoutChanges = (changes: GridLayoutChanges): boolean => Object.keys(changes).length > 0;

export const isLayoutChanged = (saved: GridLayout, current: GridLayout): boolean =>
  hasLayoutChanges(layoutChanges(saved, current));

// An "(all)" preset is shared by companies on different ERPs, whose grids have different columns. The working layout
// holds only what this grid has: the grid drops the rest when it reports, and the chips and the row requests must not
// see it. The parts of the preset about other columns are kept aside and put back when the preset is saved.

// Whether a column is this grid's: one of its data columns (`gridColumns`, from the column definitions) or a
// technical one.
const isGridColumnOf = (gridColumns: SavedColumnLayout) => {
  const ids = new Set(gridColumns.map(({ colId }) => colId));
  return (colId: string) => ids.has(colId) || isServiceColumn(colId);
};

// The part of a layout about this grid's columns: their filters, sorts and column settings. The same object when the
// layout has nothing else, so the slice keeps its reference.
export const layoutInGrid = (layout: GridLayout, gridColumns: SavedColumnLayout): GridLayout => {
  const isGridColumn = isGridColumnOf(gridColumns);
  const isComplete =
    R.keys(layout.filters).every(isGridColumn) &&
    layout.sorting.every(({ colId }) => isGridColumn(colId)) &&
    layout.columns.every(({ colId }) => isGridColumn(colId));
  return isComplete
    ? layout
    : {
        filters: R.filter(layout.filters, (_, colId) => isGridColumn(colId)),
        sorting: A.filter(layout.sorting, ({ colId }) => isGridColumn(colId)),
        columns: A.filter(layout.columns, ({ colId }) => isGridColumn(colId)),
      };
};

// `items` with the entries of `presetItems` that are not this grid's put back, each right after the entry the preset
// had before it (at the start when none of those is in `items`). `items` that is the preset without them gives the
// preset back.
const withPresetEntries = <T extends { colId: string }>(
  items: ReadonlyArray<T>,
  presetItems: ReadonlyArray<T>,
  isGridColumn: (colId: string) => boolean,
): T[] =>
  presetItems.reduce<{ result: T[]; anchor: string | null }>(
    ({ result, anchor }, item) => {
      if (isGridColumn(item.colId)) {
        return { result, anchor: result.some(({ colId }) => colId === item.colId) ? item.colId : anchor };
      }
      const at = anchor === null ? 0 : result.findIndex(({ colId }) => colId === anchor) + 1;
      return { result: [...result.slice(0, at), item, ...result.slice(at)], anchor: item.colId };
    },
    { result: [...items], anchor: null },
  ).result;

// The working layout with the preset's parts about columns this grid does not have put back: what saving the preset
// stores, so a company on another ERP keeps them.
export const withPresetPartsNotInGrid = (
  layout: GridLayout,
  preset: GridLayout,
  gridColumns: SavedColumnLayout,
): GridLayout => {
  const isGridColumn = isGridColumnOf(gridColumns);
  return {
    filters: { ...R.filter(preset.filters, (_, colId) => !isGridColumn(colId)), ...layout.filters },
    sorting: withPresetEntries(layout.sorting, preset.sorting, isGridColumn),
    columns: withPresetEntries(layout.columns, preset.columns, isGridColumn),
  };
};
